import { useState, useEffect, useCallback } from 'react';
import { useNetworkStatus } from './useNetworkStatus';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface QueuedAction {
  id: string;
  type: 'ORDER' | 'MESSAGE' | 'REVIEW' | 'DISPUTE';
  action: string;
  data: any;
  timestamp: number;
  retries: number;
}

const QUEUE_KEY = 'nazandoo_offline_queue';
const MAX_RETRIES = 3;

/**
 * Hook pour gérer la queue d'actions offline
 */
export const useOfflineQueue = () => {
  const { isOnline } = useNetworkStatus();
  const [queue, setQueue] = useState<QueuedAction[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);

  // Charger la queue depuis localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(QUEUE_KEY);
      if (saved) {
        setQueue(JSON.parse(saved));
      }
    } catch (error) {
      console.error('[Queue] Error loading:', error);
    }
  }, []);

  // Sauvegarder la queue dans localStorage
  const saveQueue = useCallback((newQueue: QueuedAction[]) => {
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(newQueue));
      setQueue(newQueue);
    } catch (error) {
      console.error('[Queue] Error saving:', error);
    }
  }, []);

  // Ajouter une action à la queue
  const addToQueue = useCallback((
    type: QueuedAction['type'],
    action: string,
    data: any
  ) => {
    const queuedAction: QueuedAction = {
      id: `${Date.now()}_${Math.random()}`,
      type,
      action,
      data,
      timestamp: Date.now(),
      retries: 0
    };

    setQueue(prev => {
      const newQueue = [...prev, queuedAction];
      saveQueue(newQueue);
      return newQueue;
    });

    console.log('[Queue] Added action:', queuedAction);
    toast.info('Action saved for later', {
      description: 'Will sync when online'
    });
  }, [saveQueue]);

  // Exécuter une action
  const executeAction = useCallback(async (queuedAction: QueuedAction): Promise<boolean> => {
    console.log('[Queue] Executing:', queuedAction);

    try {
      switch (queuedAction.type) {
        case 'MESSAGE':
          // Use encrypted message endpoint
          const { error: msgError } = await supabase.functions.invoke('encrypt-message', {
            body: {
              recipientId: queuedAction.data.recipient_id,
              subject: queuedAction.data.subject,
              message: queuedAction.data.message,
              orderId: queuedAction.data.order_id,
              disputeId: queuedAction.data.dispute_id,
            }
          });
          if (msgError) throw msgError;
          break;
        
        case 'REVIEW':
          await supabase.from('product_reviews').insert(queuedAction.data);
          break;
        
        case 'DISPUTE':
          // Appeler l'edge function pour créer le dispute
          await supabase.functions.invoke('create-dispute', {
            body: queuedAction.data
          });
          break;
        
        default:
          console.warn('[Queue] Unknown action type:', queuedAction.type);
          return false;
      }

      return true;
    } catch (error) {
      console.error('[Queue] Execution error:', error);
      return false;
    }
  }, []);

  // Synchroniser la queue
  const syncQueue = useCallback(async () => {
    if (!isOnline || queue.length === 0 || isSyncing) {
      return;
    }

    setIsSyncing(true);
    console.log(`[Queue] Syncing ${queue.length} actions...`);

    const newQueue: QueuedAction[] = [];
    let successCount = 0;
    let failedCount = 0;

    for (const action of queue) {
      const success = await executeAction(action);

      if (success) {
        successCount++;
      } else {
        // Réessayer si pas trop de tentatives
        if (action.retries < MAX_RETRIES) {
          newQueue.push({
            ...action,
            retries: action.retries + 1
          });
        } else {
          failedCount++;
          console.error('[Queue] Max retries reached for:', action);
        }
      }
    }

    saveQueue(newQueue);
    setIsSyncing(false);

    if (successCount > 0) {
      toast.success('Synchronization complete', {
        description: `${successCount} action(s) synced`
      });
    }

    if (failedCount > 0) {
      toast.error('Some actions failed', {
        description: `${failedCount} action(s) could not be synced`
      });
    }

    console.log(`[Queue] Sync complete: ${successCount} success, ${failedCount} failed, ${newQueue.length} remaining`);
  }, [isOnline, queue, isSyncing, executeAction, saveQueue]);

  // Auto-sync quand on revient online
  useEffect(() => {
    if (isOnline && queue.length > 0) {
      syncQueue();
    }
  }, [isOnline]); // Intentionnellement sans syncQueue/queue pour éviter les boucles

  return {
    queue: queue || [],
    queueCount: queue?.length || 0,
    isSyncing,
    addToQueue,
    syncQueue
  };
};
